import React, { Fragment, useEffect, useState } from 'react';
import { graphql, Link, withPrefix } from 'gatsby';
import styled from 'styled-components';
import { ThemeProvider } from 'styled-components';
import { theme } from 'common/theme/app';
import { ResetCSS } from 'common/assets/css/style';
import { GlobalStyle } from 'containers/App/app.style';
import Seo from 'components/seo';
import BlogSidebar from 'components/BlogSidebar';
import { isRunningInAppWebView } from 'common/components/AppModeDetector';
import AppAwareLink from 'components/BlogSidebar/AppAwareLink';
import ContentPage from 'layouts/ContentPage';
import SiteLayout from 'layouts/SiteLayout';

const BlogPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 2.5rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
  
  /* Tighten spacing for app views */
  .app-view & {
    padding: 0 1rem 20px;
  }
`;

const MainContent = styled.article`
  width: 100%;
  min-width: 0;
`;

const BlogHeader = styled.header`
  margin: 0 0 2rem;
  
  /* Tighter spacing for app views */
  .app-view & {
    margin: 0 0 0.75rem;
  }
`;

const BlogTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.headingColor};
  line-height: 1.2;
  
  /* Smaller font for mobile app views - match blog list view */
  .app-view & {
    font-size: 1.4rem;
    margin-bottom: 0.3rem;
    line-height: 1.3;
  }
`;

const BlogMeta = styled.div`
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  color: ${props => props.theme.colors.textColor};
  opacity: 0.8;
  
  /* Adjust for app view */
  .app-view & {
    margin-bottom: 0.5rem;
    font-size: 0.85rem;
    gap: 0.5rem;
  }
`;

const BlogDate = styled.time`
  font-weight: 500;
`;

const BlogCategory = styled(Link)`
  color: ${props => props.theme.colors.secondaryDark};
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BlogImageWrapper = styled.div`
  margin-bottom: 2rem;
  border-radius: 12px;
  overflow: hidden;
  max-height: 500px;
  
  /* Tighter spacing for app views */
  .app-view & {
    margin: 0.3rem 0 0.75rem;
    border-radius: 8px;
  }
  
  img {
    display: block;
    width: 100%;
    height: 500px;
    object-fit: cover;
    
    /* Allow flexible height in app view */
    .app-view & {
      height: 200px;
      max-height: 250px;
    }
  }
`;

const BlogContent = styled.div`
  line-height: 1.8;
  color: ${props => props.theme.colors.textColor};
  font-size: 1.05rem;
  
  /* Adjust spacing for app views */
  .app-view & {
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  img {
    max-width: 100%;
    height: auto;
    max-height: 500px;
    object-fit: contain;
    border-radius: 8px;
    margin: 2rem 0;
    
    /* Adjust spacing for app views */
    .app-view & {
      margin: 0.75rem 0;
      max-height: 250px;
    }
  }

  h2, h3, h4, h5, h6 {
    color: ${props => props.theme.colors.headingColor};
    margin: 2.5rem 0 1.2rem;
    line-height: 1.3;
    
    /* Adjust spacing for app views */
    .app-view & {
      margin: 1.2rem 0 0.6rem;
    }
  }
  
  h2 {
    font-size: 1.8rem;
    
    /* Adjust size for app views */
    .app-view & {
      font-size: 1.3rem;
    }
  }
  
  h3 {
    font-size: 1.5rem;
    
    /* Adjust size for app views */
    .app-view & {
      font-size: 1.1rem;
    }
  }

  p {
    margin-bottom: 1.8rem;
    
    /* Adjust spacing for app views */
    .app-view & {
      margin-bottom: 0.8rem;
    }
  }

  a {
    color: ${props => props.theme.colors.secondaryDark};
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.3s ease;
    
    &:hover {
      border-color: ${props => props.theme.colors.primary};
    }
  }

  ul, ol {
    margin-bottom: 1.8rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.8rem;
    }
  }

  blockquote {
    margin: 2.5rem 0;
    padding: 1.5rem 2rem;
    border-left: 4px solid ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.lightBg || '#f7f7f7'};
    font-style: italic;
    border-radius: 0 8px 8px 0;
  }
  
  pre {
    background: #f1f1f1;
    padding: 1.5rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 2rem 0;
  }
  
  code {
    background: #f1f1f1;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-size: 0.9rem;
  }
`;

const BackToBlogs = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &:before {
    content: "←";
    margin-right: 8px;
  }
`;

const TagsList = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const Tag = styled.span`
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.textColor};
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
`;

const RelatedPosts = styled.div`
  margin-top: 3rem;
  border-top: 1px solid #eaeaea;
  padding-top: 2rem;
`;

const RelatedPostsTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.headingColor};
`;

const RelatedPostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const RelatedPostCard = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const RelatedPostImage = styled.div`
  height: 160px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  ${RelatedPostCard}:hover img {
    transform: scale(1.05);
  }
`;

const RelatedPostContent = styled.div`
  padding: 1rem;
`;

const RelatedPostDate = styled.time`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textColor};
  opacity: 0.7;
  display: block;
  margin-bottom: 0.5rem;
`;

const RelatedPostTitle = styled.h4`
  font-size: 1rem;
  margin: 0;
  line-height: 1.4;
  color: ${props => props.theme.colors.headingColor};
  transition: color 0.2s ease;
  
  ${RelatedPostCard}:hover & {
    color: ${props => props.theme.colors.primary};
  }
`;

const BlogPost = ({ data, hideHeaderFooter = false, location }) => {
  // Use state for the combined value, not just the app detection
  const [shouldHideHeaderFooter, setShouldHideHeaderFooter] = useState(false);
  
  // Check URL parameters for app mode - highest priority
  const isAppMode = location && location.search ? 
    new URLSearchParams(location.search).get('app') === 'true' : false;
  
  useEffect(() => {
    // If app=true parameter is present, apply immediately to avoid flicker
    if (isAppMode) {
      setShouldHideHeaderFooter(true);
      return;
    }
    
    // Only check for WebView detection if not already determined by URL parameter
    if (typeof window !== 'undefined') {
      setShouldHideHeaderFooter(hideHeaderFooter || isRunningInAppWebView());
    }
  }, [hideHeaderFooter, isAppMode]);
  
  const { markdownRemark: post } = data;
  
  // Process image path to handle static images
  const processImagePath = (path) => {
    if (!path) return null;
    
    // If path starts with '/', it's relative to the static folder
    if (typeof path === 'string' && path.startsWith('/')) {
      return withPrefix(path);
    }
    
    return path;
  };

  const imagePath = processImagePath(post.frontmatter.thumbnail);
  
  // Get related posts - we can use filtered post data from the same query
  const relatedPosts = data.relatedPosts.edges;

  // Content section that can be used in both web and mobile contexts
  const BlogPostContent = () => (
    <Fragment>
      <Seo 
        title={post.frontmatter.title} 
        description={post.frontmatter.description || post.excerpt} 
        keywords={post.frontmatter.tags || []}
        isBlogPage={true}
      />
      
      <BlogPageContainer>
        <MainContent>
          {!shouldHideHeaderFooter && <BackToBlogs as={AppAwareLink} to="/blog">Back to all blogs</BackToBlogs>}
          
          <BlogHeader>
            <BlogTitle>{post.frontmatter.title}</BlogTitle>
            <BlogMeta>
              {!shouldHideHeaderFooter && <BlogDate>{post.frontmatter.date}</BlogDate>}
              {post.frontmatter.category && !shouldHideHeaderFooter && (
                <BlogCategory as={AppAwareLink} to={`/blog/category/${post.frontmatter.category.toLowerCase().replace(/ /g, '-')}`}>
                  {post.frontmatter.category}
                </BlogCategory>
              )}
              {post.frontmatter.category && shouldHideHeaderFooter && (
                <span style={{ color: theme.colors.primary, fontWeight: 500 }}>
                  {post.frontmatter.category}
                </span>
              )}
            </BlogMeta>
            
            {imagePath && (
              <BlogImageWrapper>
                <img 
                  src={imagePath}
                  alt={post.frontmatter.title}
                />
              </BlogImageWrapper>
            )}
          </BlogHeader>
          
          <BlogContent dangerouslySetInnerHTML={{ __html: post.html }} />
          
          {!shouldHideHeaderFooter && post.frontmatter.tags && post.frontmatter.tags.length > 0 && (
            <TagsList>
              {post.frontmatter.tags.map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </TagsList>
          )}
          
          {!shouldHideHeaderFooter && relatedPosts.length > 0 && (
            <RelatedPosts>
              <RelatedPostsTitle>Related posts</RelatedPostsTitle>
              <RelatedPostsGrid>
                {relatedPosts.map(({ node }) => {
                  const relatedTitle = node.frontmatter.title;
                  const relatedThumbnail = processImagePath(node.frontmatter.thumbnail);
                  
                  return (
                    <RelatedPostCard 
                      as={AppAwareLink} 
                      to={node.fields.slug} 
                      key={node.id}
                    >
                      <RelatedPostImage>
                        <img 
                          src={relatedThumbnail}
                          alt={relatedTitle}
                        />
                      </RelatedPostImage>
                      <RelatedPostContent>
                        <RelatedPostDate>{node.frontmatter.date}</RelatedPostDate>
                        <RelatedPostTitle>{relatedTitle}</RelatedPostTitle>
                      </RelatedPostContent>
                    </RelatedPostCard>
                  );
                })}
              </RelatedPostsGrid>
            </RelatedPosts>
          )}
        </MainContent>
        
        {!shouldHideHeaderFooter && <BlogSidebar currentCategory={post.frontmatter.category} />}
      </BlogPageContainer>
    </Fragment>
  );

  // For mobile app usage, we can return just the content without wrapper
  if (shouldHideHeaderFooter || isAppMode) {
    return (
      <ThemeProvider theme={theme}>
        <Fragment>
          <ResetCSS />
          <GlobalStyle />
          <div className="app-view" style={{ padding: 0, margin: 0 }}>
            <BlogPostContent />
          </div>
        </Fragment>
      </ThemeProvider>
    );
  }

  // For web usage, include the full layout
  return (
    <SiteLayout>
        <ContentPage
          title={post.frontmatter.title}
          description={post.frontmatter.description || post.excerpt.substring(0, 120)}
          eyebrow="Furbuz journal"
          width="1200px"
        >
          <BlogPostContent />
        </ContentPage>
    </SiteLayout>
  );
};

// Export the content component for reuse in mobile apps
export const BlogPostContent = ({ data }) => {
  return <BlogPost data={data} hideHeaderFooter={true} />;
};

export const query = graphql`
  query BlogPostBySlug($slug: String!, $category: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      excerpt(pruneLength: 160)
      frontmatter {
        title
        date(formatString: "DD.MMMM.YYYY")
        description
        thumbnail
        tags
        category
      }
    }
    relatedPosts: allMarkdownRemark(
      filter: {
        fields: { slug: { ne: $slug } }
        frontmatter: { category: { eq: $category } }
      }
      limit: 4
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      edges {
        node {
          fields {
            slug
          }
          frontmatter {
            title
            date(formatString: "DD.MMMM.YYYY")
            thumbnail
          }
        }
      }
    }
  }
`;

export default BlogPost; 